import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useExtractionStore, type BboxItem } from '../../store/useExtractionStore';
import { useSSEStore, useIsExtractingDiagrams } from '../../store/useSSEStore';
import { useBboxDrag } from '../../hooks/useBboxDrag';
import * as api from '../../api/endpoints';
import { withProjectQuery } from '../../api/projectContext';
import { parseBboxCoords, parseContextBlocks } from './utils';

const ZOOM_CONFIG = {
  MIN: 0.1,
  MAX: 10.0,
  BUTTON_STEP: 0.2,
  WHEEL_SENSITIVITY: 0.2,
  WHEEL_PIXEL_MODE_MULTIPLIER: 0.01,
  WHEEL_LINE_MODE_MULTIPLIER: 0.05,
  WHEEL_MAX_DELTA: 1,
  TILE_ZOOM_THRESHOLD: 1.5,
} as const;

interface PageRenderInfo {
  display_url: string;
  tiles_url_prefix: string | null;
  full_res_url: string;
  width: number;
  height: number;
  display_width: number;
  display_height: number;
  tile_cols: number;
  tile_rows: number;
  tile_size: number;
}

interface TileEntry {
  row: number;
  col: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

function fileStem(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.substring(0, dot) : filename;
}

export function ExtractionLeft() {
  const pages = useExtractionStore((s) => s.pages);
  const pageIndex = useExtractionStore((s) => s.pageIndex);
  const zoom = useExtractionStore((s) => s.zoom);
  const setPageIndex = useExtractionStore((s) => s.setPageIndex);
  const setZoom = useExtractionStore((s) => s.setZoom);
  const setCurrentBboxes = useExtractionStore((s) => s.setCurrentBboxes);
  const setPageBboxes = useExtractionStore((s) => s.setPageBboxes);
  const currentReview = useExtractionStore((s) => s.currentReview);

  const viewportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Image dimensions for bbox coordinate system
  const [baseDims, setBaseDims] = useState({ w: 0, h: 0 });
  const baseDimsRef = useRef({ w: 0, h: 0 });
  baseDimsRef.current = baseDims;

  // Pan+zoom via refs (single source of truth) + imperative DOM updates
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(zoom);
  const rafId = useRef(0);
  const [, setZoomDisplay] = useState(zoom);

  // Server-rendered page info
  const [pageInfo, setPageInfo] = useState<PageRenderInfo | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Visible tiles for high-zoom rendering
  const [visibleTiles, setVisibleTiles] = useState<TileEntry[]>([]);
  const tileTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const applyTransform = useCallback((z: number, pan: { x: number; y: number }) => {
    if (viewportRef.current) {
      viewportRef.current.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${z})`;
    }
  }, []);

  const scheduleDisplaySync = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      setZoomDisplay(zoomRef.current);
    });
  }, []);

  const setTransform = useCallback((z: number, pan: { x: number; y: number }) => {
    zoomRef.current = z;
    panOffsetRef.current = pan;
    applyTransform(z, pan);
    setZoomDisplay(z);
    setZoom(z);
  }, [applyTransform, setZoom]);

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dragStartCoords = useRef({ x: 0, y: 0 });

  const currentPage = pages[pageIndex];

  // ── Bbox state management ──────────────────────────────────
  const [bboxes, setBboxes] = useState<BboxItem[]>([]);
  const nextBboxId = useRef(0);
  const bboxPageKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setCurrentBboxes(bboxes);
    if (bboxPageKeyRef.current) {
      setPageBboxes(bboxPageKeyRef.current, bboxes);
    }
  }, [bboxes, setCurrentBboxes, setPageBboxes]);

  const handleDragEnd = useCallback((el: HTMLDivElement) => {
    const { w, h } = baseDimsRef.current;
    if (!w || !h) return;
    const bboxId = el.dataset.bboxId;
    if (!bboxId) return;

    const left = parseFloat(el.style.left) || 0;
    const top = parseFloat(el.style.top) || 0;
    const elW = el.offsetWidth;
    const elH = el.offsetHeight;

    const newBbox: [number, number, number, number] = [
      left / w, top / h, (left + elW) / w, (top + elH) / h,
    ];
    setBboxes(prev => prev.map(b =>
      b.id === bboxId ? { ...b, bbox: newBbox } : b
    ));
  }, [setBboxes]);

  const { startDrag, startResize } = useBboxDrag(zoom, handleDragEnd);

  // Initialize bboxes from classification, review, or saved user edits when page changes
  useEffect(() => {
    const review = currentReview;
    const classification = currentPage?.classification;
    const pageKey = currentPage ? `${currentPage.file}:${currentPage.page}` : null;

    bboxPageKeyRef.current = pageKey;

    const reviewMatchesPage = review && currentPage &&
      review.file === currentPage.file && review.page === currentPage.page;

    const savedBboxes = pageKey
      ? useExtractionStore.getState().pageBboxes[pageKey]
      : undefined;

    if (reviewMatchesPage && review) {
      const result: BboxItem[] = [];
      const cb = parseBboxCoords(review.content_bbox);
      if (cb) {
        result.push({ id: `b-${nextBboxId.current++}`, type: 'content', bbox: cb });
      }
      result.push(...parseContextBlocks(review.context_blocks as unknown[] || [], nextBboxId));
      setBboxes(result);
    } else if (savedBboxes && savedBboxes.length > 0) {
      setBboxes(savedBboxes);
    } else if (classification) {
      const result: BboxItem[] = [];
      const cb = parseBboxCoords(classification.content_bbox);
      if (cb) {
        result.push({ id: `b-${nextBboxId.current++}`, type: 'content', bbox: cb });
      }
      result.push(...parseContextBlocks(classification.context_blocks as unknown[] || [], nextBboxId));
      setBboxes(result);
    } else {
      setBboxes([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, currentReview, currentPage?.file, currentPage?.page]);

  const addCrop = useCallback((type: string) => {
    setBboxes((prev: BboxItem[]) => {
      const sameTypeCount = prev.filter(b => b.type === type).length;
      const offset = sameTypeCount * 0.05;
      return [...prev, {
        id: `b-${nextBboxId.current++}`,
        type,
        bbox: [0.1 + offset, 0.1 + offset, 0.9, 0.9] as [number, number, number, number],
        label: type,
      }];
    });
  }, [setBboxes]);

  const deleteBbox = useCallback((id: string) => {
    setBboxes(prev => prev.filter(b => b.id !== id));
  }, [setBboxes]);

  // ── Server-side page rendering ─────────────────────────────

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Prepare page image on server when page changes
  useEffect(() => {
    if (!currentPage || !containerWidth) return;
    let cancelled = false;
    setPreparing(true);
    setPageInfo(null);
    setPrepareError(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 120_000); // 2 min timeout

    console.log('[Extraction] Preparing page:', currentPage.file, 'p.', currentPage.page);

    api.preparePage(currentPage.file, currentPage.page).then((info) => {
      clearTimeout(timeout);
      if (cancelled) return;
      if (!info) {
        console.error('[Extraction] preparePage returned empty');
        setPrepareError('Server returned empty response');
        setPreparing(false);
        return;
      }
      console.log('[Extraction] Page ready:', info.width, 'x', info.height, 'tiles:', info.tile_cols, 'x', info.tile_rows);
      setPageInfo(info);
      setBaseDims({ w: info.width, h: info.height });
      setPreparing(false);
    }).catch((err) => {
      clearTimeout(timeout);
      if (!cancelled) {
        const msg = controller.signal.aborted ? 'Render timed out (>2min)' : String(err);
        console.error('[Extraction] Failed to prepare page:', msg);
        setPrepareError(msg);
        setPreparing(false);
      }
    });

    return () => { cancelled = true; clearTimeout(timeout); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage?.file, currentPage?.page, containerWidth, retryCount]);

  const navigate = (delta: number) => {
    const next = pageIndex + delta;
    if (next >= 0 && next < pages.length) setPageIndex(next);
  };

  // ── Tile visibility ──────────────────────────────────────────

  const recomputeTiles = useCallback(() => {
    if (!pageInfo || !pageInfo.tile_cols || !containerRef.current) {
      setVisibleTiles([]);
      return;
    }
    const z = zoomRef.current;
    if (z < ZOOM_CONFIG.TILE_ZOOM_THRESHOLD) {
      setVisibleTiles([]);
      return;
    }

    const scale = baseDims.w / pageInfo.width;
    const tileCssW = pageInfo.tile_size * scale;
    const tileCssH = pageInfo.tile_size * scale;

    const pan = panOffsetRef.current;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;

    // Visible area in image-space (CSS coords before transform)
    const vx0 = -pan.x / z;
    const vy0 = -pan.y / z;
    const vx1 = (cw - pan.x) / z;
    const vy1 = (ch - pan.y) / z;

    // Tile range with 1-tile buffer
    const c0 = Math.max(0, Math.floor(vx0 / tileCssW) - 1);
    const c1 = Math.min(pageInfo.tile_cols - 1, Math.ceil(vx1 / tileCssW));
    const r0 = Math.max(0, Math.floor(vy0 / tileCssH) - 1);
    const r1 = Math.min(pageInfo.tile_rows - 1, Math.ceil(vy1 / tileCssH));

    const tiles: TileEntry[] = [];
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        tiles.push({
          row: r, col: c,
          x: c * tileCssW, y: r * tileCssH,
          w: tileCssW, h: tileCssH,
        });
      }
    }
    setVisibleTiles(tiles);
  }, [pageInfo, baseDims.w]);

  const scheduleTileUpdate = useCallback(() => {
    if (tileTimerRef.current) clearTimeout(tileTimerRef.current);
    tileTimerRef.current = setTimeout(recomputeTiles, 100);
  }, [recomputeTiles]);

  // Recompute tiles when page info or dimensions change
  useEffect(() => {
    recomputeTiles();
  }, [recomputeTiles]);

  // ── Zoom to fit ──────────────────────────────────────────────
  const zoomToFit = useCallback(() => {
    const container = containerRef.current;
    if (!container || !baseDims.w || !baseDims.h) return;
    const rect = container.getBoundingClientRect();
    const scaleX = rect.width / baseDims.w;
    const scaleY = rect.height / baseDims.h;
    const fitZoom = Math.min(scaleX, scaleY, 1);
    const renderedW = baseDims.w * fitZoom;
    const renderedH = baseDims.h * fitZoom;
    const centerX = (rect.width - renderedW) / 2;
    const centerY = (rect.height - renderedH) / 2;
    setTransform(fitZoom, { x: centerX, y: centerY });
  }, [baseDims, setTransform]);

  // Auto zoom-to-fit when base dims change (new page rendered)
  useEffect(() => {
    if (baseDims.w > 0 && baseDims.h > 0) {
      requestAnimationFrame(() => zoomToFit());
    }
  }, [baseDims, zoomToFit]);

  // ── Pointer-centered zoom (exponential) ──────────────────────
  const handleZoom = useCallback((
    directionOrEvent: 'in' | 'out' | WheelEvent,
  ) => {
    if (!containerRef.current) return;

    const curZoom = zoomRef.current;
    const curPan = panOffsetRef.current;

    let delta: number;
    let zoomPoint: { x: number; y: number } | null = null;

    if (typeof directionOrEvent === 'string') {
      delta = directionOrEvent === 'in' ? ZOOM_CONFIG.BUTTON_STEP : -ZOOM_CONFIG.BUTTON_STEP;
    } else {
      const event = directionOrEvent;
      delta = event.deltaY;

      if (event.deltaMode === 0) {
        delta *= ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER;
      } else if (event.deltaMode === 1) {
        delta *= ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER;
      }

      delta = Math.max(-ZOOM_CONFIG.WHEEL_MAX_DELTA, Math.min(ZOOM_CONFIG.WHEEL_MAX_DELTA, delta));
      delta = -delta;

      zoomPoint = { x: event.clientX, y: event.clientY };
    }

    const factor = Math.exp(delta * ZOOM_CONFIG.WHEEL_SENSITIVITY);
    const newZoom = curZoom * factor;
    const clampedZoom = Math.max(ZOOM_CONFIG.MIN, Math.min(ZOOM_CONFIG.MAX, newZoom));

    const containerRect = containerRef.current.getBoundingClientRect();
    const mx = zoomPoint ? zoomPoint.x - containerRect.left : containerRect.width / 2;
    const my = zoomPoint ? zoomPoint.y - containerRect.top : containerRect.height / 2;

    const imageX = (mx - curPan.x) / curZoom;
    const imageY = (my - curPan.y) / curZoom;
    const newPan = { x: mx - imageX * clampedZoom, y: my - imageY * clampedZoom };

    zoomRef.current = clampedZoom;
    panOffsetRef.current = newPan;

    applyTransform(clampedZoom, newPan);
    setZoom(clampedZoom);
    scheduleDisplaySync();
    scheduleTileUpdate();
  }, [applyTransform, scheduleDisplaySync, setZoom, scheduleTileUpdate]);

  // Native wheel listener with { passive: false }
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      handleZoom(e);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [handleZoom]);

  // ── Drag panning ──────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.bbox-overlay')) return;
    if (e.button !== 0) return;
    const curPan = panOffsetRef.current;
    setIsDragging(true);
    setDragStart({ x: e.clientX - curPan.x, y: e.clientY - curPan.y });
    dragStartCoords.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const newPan = { x: event.clientX - dragStart.x, y: event.clientY - dragStart.y };
    panOffsetRef.current = newPan;
    applyTransform(zoomRef.current, newPan);
    scheduleTileUpdate();
  }, [dragStart, applyTransform, scheduleTileUpdate]);

  const handleMouseUp = useCallback((event: MouseEvent) => {
    setIsDragging(false);
    const dx = Math.abs(event.clientX - dragStartCoords.current.x);
    const dy = Math.abs(event.clientY - dragStartCoords.current.y);
    if (dx < 3 && dy < 3) {
      // click-through — nothing to do
    }
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ── Derived values for render ─────────────────────────────────
  const stemForUrl = currentPage ? fileStem(currentPage.file) : '';
  const dimsW = baseDims.w;
  const dimsH = baseDims.h;

  return (
    <div className="extraction-left">
      <div className="page-nav">
        <button className="nav-btn" disabled={pageIndex <= 0} onClick={() => navigate(-1)}>
          &larr; Prev
        </button>
        <select
          value={pageIndex}
          onChange={(e) => setPageIndex(Number(e.target.value))}
        >
          {pages.map((p, i) => (
            <option key={i} value={i}>
              {p.file} p.{p.page} — {p.docType}
            </option>
          ))}
        </select>
        <button
          className="nav-btn"
          disabled={pageIndex >= pages.length - 1}
          onClick={() => navigate(1)}
        >
          Next &rarr;
        </button>
        <span className="page-info">
          {pages.length > 0 ? `${pageIndex + 1} / ${pages.length}` : 'No pages'}
        </span>
      </div>

      <CropToolbar addCrop={addCrop} />
      <ExtractAllPanel />

      <div className="extraction-viewer-area">
        <div
          className="page-review-viewer"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            overflow: 'hidden',
          }}
        >
          <div
            ref={viewportRef}
            className="extraction-viewport"
            style={{ transformOrigin: '0 0' }}
          >
            {/* Server-rendered full-res image */}
            {pageInfo && (
              <img
                src={withProjectQuery(pageInfo.full_res_url)}
                draggable={false}
                onError={(e) => {
                  console.error('[Extraction] Image load failed:', withProjectQuery(pageInfo.full_res_url));
                  (e.target as HTMLImageElement).style.display = 'none';
                  setPrepareError('Image failed to load — try refreshing');
                }}
                style={{
                  width: dimsW,
                  height: dimsH,
                  display: 'block',
                  imageRendering: 'auto',
                }}
              />
            )}
            {preparing && !pageInfo && (
              <div style={{ padding: 24, color: 'var(--text-secondary)' }}>
                Rendering page...
              </div>
            )}
            {prepareError && !preparing && (
              <div style={{ padding: 24, color: 'var(--danger)', display: 'flex', gap: 12, alignItems: 'center' }}>
                <span>{prepareError}</span>
                <button
                  className="nav-btn"
                  onClick={() => { setPrepareError(null); setRetryCount(c => c + 1); }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* High-res tiles overlaid at zoom > threshold */}
            {visibleTiles.length > 0 && currentPage && visibleTiles.map((t) => (
              <img
                key={`tile_${t.row}_${t.col}`}
                src={api.pageTileUrl(stemForUrl, currentPage.page, t.row, t.col)}
                draggable={false}
                onError={(e) => {
                  console.warn(`[Extraction] Tile ${t.row},${t.col} failed to load`);
                  (e.target as HTMLImageElement).style.opacity = '0';
                }}
                style={{
                  position: 'absolute',
                  left: t.x,
                  top: t.y,
                  width: t.w,
                  height: t.h,
                  imageRendering: 'auto',
                }}
              />
            ))}

            {/* Bbox overlays */}
            {dimsW > 0 && bboxes.map(b => {
              const [xMin, yMin, xMax, yMax] = b.bbox;
              return (
                <BboxOverlay
                  key={b.id}
                  bboxId={b.id}
                  type={b.type}
                  label={b.label}
                  left={xMin * dimsW}
                  top={yMin * dimsH}
                  width={(xMax - xMin) * dimsW}
                  height={(yMax - yMin) * dimsH}
                  canvasW={dimsW}
                  canvasH={dimsH}
                  startDrag={startDrag}
                  startResize={startResize}
                  onDelete={() => deleteBbox(b.id)}
                />
              );
            })}
          </div>
        </div>

        <div className="extraction-bottom-bar">
          <div className="page-review-legend">
            <span><span className="swatch content" />Content</span>
            <span><span className="swatch legend" />Legend</span>
            <span><span className="swatch title_block" />Title Block</span>
            <span><span className="swatch notes" />Notes</span>
          </div>

          <div className="zoom-controls">
            <button onClick={() => handleZoom('out')} title="Zoom out">−</button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => handleZoom('in')} title="Zoom in">+</button>
            <button onClick={zoomToFit} title="Fit to view" style={{ fontSize: 11 }}>Fit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bbox Overlay ─────────────────────────────────────────────

function BboxOverlay({
  bboxId,
  type,
  label,
  left,
  top,
  width,
  height,
  canvasW,
  canvasH,
  startDrag,
  startResize,
  onDelete,
}: {
  bboxId: string;
  type: string;
  label?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  canvasW: number;
  canvasH: number;
  startDrag: (e: React.MouseEvent, div: HTMLDivElement, w: number, h: number) => void;
  startResize: (e: React.MouseEvent, div: HTMLDivElement, corner: string, w: number, h: number) => void;
  onDelete: () => void;
}) {
  const divRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={divRef}
      className={`bbox-overlay bbox-${type}`}
      data-bbox-type={type}
      data-bbox-label={label}
      data-bbox-id={bboxId}
      style={{
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      }}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).classList.contains('bbox-handle')) return;
        if ((e.target as HTMLElement).classList.contains('bbox-delete')) return;
        if (divRef.current) startDrag(e, divRef.current, canvasW, canvasH);
      }}
    >
      <span className="bbox-label">{label || type}</span>
      <button className="bbox-delete" onClick={onDelete}>&times;</button>
      {['nw', 'ne', 'sw', 'se'].map((corner) => (
        <div
          key={corner}
          className={`bbox-handle bbox-handle-${corner}`}
          onMouseDown={(e) => {
            if (divRef.current) startResize(e, divRef.current, corner, canvasW, canvasH);
          }}
        />
      ))}
    </div>
  );
}

// ── Crop Toolbar ────────────────────────────────────────────

function CropToolbar({ addCrop }: { addCrop: (type: string) => void }) {
  const autoApprove = useExtractionStore((s) => s.autoApprovePages);
  const setAutoApprove = useExtractionStore((s) => s.setAutoApprovePages);
  const currentBboxes = useExtractionStore((s) => s.currentBboxes);
  const [extractingAll, setExtractingAll] = useState(false);
  // Running by either path (auto post-research run OR this Extract All button)
  const isExtracting = useIsExtractingDiagrams();

  // Count bboxes by type
  const counts = useMemo(() => {
    const c: Record<string, number> = { content: 0, legend: 0, title_block: 0, notes: 0 };
    for (const b of currentBboxes) {
      c[b.type] = (c[b.type] || 0) + 1;
    }
    return c;
  }, [currentBboxes]);

  const handleAutoApprove = async () => {
    const newVal = !autoApprove;
    setAutoApprove(newVal);
    if (newVal) {
      const ext = useExtractionStore.getState();
      const toApprove: { requestId: string; key: string }[] = [];
      if (ext.currentReview) {
        toApprove.push({
          requestId: ext.currentReview.request_id,
          key: `${ext.currentReview.file}:${ext.currentReview.page}`,
        });
        ext.setCurrentReview(null);
      }
      for (const r of ext.pendingReviews) {
        toApprove.push({ requestId: r.request_id, key: `${r.file}:${r.page}` });
      }
      ext.clearPendingReviews();
      for (const item of toApprove) {
        ext.setPageStatus(item.key, 'extracting');
      }
      try {
        await Promise.all(toApprove.map(item => api.approvePageReview(item.requestId)));
      } catch (e) {
        useProjectStore.getState().setToast(`Auto-approve failed: ${e}`, 'error');
      }
    }
  };

  return (
    <div className="extraction-crop-toolbar">
      <button className="btn-add-crop btn-crop-content" onClick={() => addCrop('content')}>
        + Content{counts.content > 0 ? ` (${counts.content})` : ''}
      </button>
      <button className="btn-add-crop btn-crop-legend" onClick={() => addCrop('legend')}>
        + Legend{counts.legend > 0 ? ` (${counts.legend})` : ''}
      </button>
      <button className="btn-add-crop btn-crop-title" onClick={() => addCrop('title_block')}>
        + Title{counts.title_block > 0 ? ` (${counts.title_block})` : ''}
      </button>
      <button className="btn-add-crop btn-crop-notes" onClick={() => addCrop('notes')}>
        + Notes{counts.notes > 0 ? ` (${counts.notes})` : ''}
      </button>
      <button
        className={`btn-auto-approve ${autoApprove ? 'active' : ''}`}
        onClick={handleAutoApprove}
      >
        {autoApprove ? 'Auto-approve ON' : 'Auto-approve Remaining'}
      </button>
      <button
        className="btn-extract-all"
        onClick={async () => {
          if (extractingAll) return;
          if (!confirm('Extract every diagram PDF in parallel? Existing results for each file will be replaced.')) return;
          setExtractingAll(true);
          try {
            const res = await api.extractAll({ max_parallel: 4 });
            if (res) {
              useProjectStore.getState().setToast(
                `Extract All done — ${res.files_processed} file(s), ${res.total_components} components` +
                  (res.files_failed ? ` (${res.files_failed} failed)` : ''),
                res.files_failed ? 'error' : 'success',
              );
            }
          } catch (e) {
            useProjectStore.getState().setToast(`Extract All failed: ${e}`, 'error');
          } finally {
            setExtractingAll(false);
            setTimeout(() => useSSEStore.getState().clearSkillProgress('extract_all'), 4000);
          }
        }}
        disabled={extractingAll || isExtracting}
        title="Extract every diagram document in parallel"
      >
        {extractingAll || isExtracting ? 'Extracting…' : '⚡ Extract All'}
      </button>
    </div>
  );
}

// ── Extract All Progress Panel ──────────────────────────────
// Lives below the toolbar; shows per-file page progress while extract-all
// is running. Computes file totals from triage pages + extractable doc types,
// tracks completed pages via pageStatuses (updated by page_complete SSE).

const EXTRACTABLE_DOC_TYPES = new Set([
  'single_line_diagram', 'panel_schedule', 'circuit_diagram',
  'wiring_diagram', 'diagram', 'cable_schedule',
]);

function ExtractAllPanel() {
  const progressEa = useSSEStore((s) => s.skillProgress['extract_all']);
  const progressAuto = useSSEStore((s) => s.skillProgress['diagram_extraction']);
  const isExtracting = useIsExtractingDiagrams();
  const pages = useExtractionStore((s) => s.pages);
  const pageStatuses = useExtractionStore((s) => s.pageStatuses);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  // Track start time
  useEffect(() => {
    if (isExtracting && startedAt === null) setStartedAt(Date.now());
    if (!isExtracting && startedAt !== null) setStartedAt(null);
  }, [isExtracting, startedAt]);

  // Ticking clock for "elapsed" display
  useEffect(() => {
    if (!isExtracting) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isExtracting]);

  // Build per-file progress from pages + pageStatuses. Only meaningful when
  // per-page events flow (local runs + manual Extract All). The prod auto-run
  // forwards only aggregate progress, so these stay at done=0 and we hide them.
  const files = useMemo(() => {
    const byFile = new Map<string, { file: string; total: number; done: number }>();
    for (const p of pages) {
      if (!EXTRACTABLE_DOC_TYPES.has(p.docType as string)) continue;
      const e = byFile.get(p.file) || { file: p.file, total: 0, done: 0 };
      e.total += 1;
      const st = pageStatuses[`${p.file}:${p.page}`];
      if (st === 'done') e.done += 1;
      byFile.set(p.file, e);
    }
    return Array.from(byFile.values()).sort((a, b) => {
      // Sort: running first, then queued, then done
      const aDone = a.done >= a.total;
      const bDone = b.done >= b.total;
      const aRunning = a.done > 0 && !aDone;
      const bRunning = b.done > 0 && !bDone;
      if (aRunning !== bRunning) return aRunning ? -1 : 1;
      if (aDone !== bDone) return aDone ? 1 : -1;
      return a.file.localeCompare(b.file);
    });
  }, [pages, pageStatuses]);

  // Show the rich per-file breakdown only when we truly have per-file progress.
  const hasPerFileData = useMemo(
    () => files.some((f) => f.done > 0) ||
      Object.values(pageStatuses).some((s) => s === 'extracting' || s === 'done'),
    [files, pageStatuses],
  );

  if (!isExtracting) return null;

  // Pages = the common unit. With per-file data, sum it; otherwise fall back to
  // the active aggregate (Extract All counts files, the auto-run counts pages).
  const aggregate = progressEa ?? progressAuto;
  const aggUnit = progressEa ? 'files' : 'pages';
  const pagesTotal = hasPerFileData ? files.reduce((s, f) => s + f.total, 0) : (aggregate?.total ?? 0);
  const pagesDone = hasPerFileData ? files.reduce((s, f) => s + f.done, 0) : (aggregate?.processed ?? 0);
  const filesTotal = files.length;
  const filesDone = files.filter((f) => f.total > 0 && f.done >= f.total).length;
  const pagePct = pagesTotal ? (pagesDone / pagesTotal) * 100 : 0;

  const elapsedMs = startedAt ? now - startedAt : 0;
  const mins = Math.floor(elapsedMs / 60000);
  const secs = Math.floor((elapsedMs % 60000) / 1000);

  return (
    <div
      style={{
        margin: '6px 12px 12px',
        padding: '10px 14px',
        background: 'linear-gradient(180deg, #1e293b 0%, #172033 100%)',
        border: '1px solid #334155',
        borderRadius: 10,
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        color: '#e2e8f0',
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 4,
              background: '#3b82f6',
              boxShadow: '0 0 8px #3b82f6',
              animation: 'pulse 1.6s ease-in-out infinite',
            }}
          />
          <span style={{ fontWeight: 600, color: '#f1f5f9' }}>
            Extracting all diagrams
          </span>
          <span style={{ color: '#94a3b8' }}>·</span>
          {hasPerFileData ? (
            <>
              <span style={{ color: '#cbd5e1' }}>
                <strong style={{ color: '#f1f5f9' }}>{pagesDone}</strong>/{pagesTotal} pages
              </span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span style={{ color: '#cbd5e1' }}>
                <strong style={{ color: '#f1f5f9' }}>{filesDone}</strong>/{filesTotal} files
              </span>
            </>
          ) : (
            <span style={{ color: '#cbd5e1' }}>
              <strong style={{ color: '#f1f5f9' }}>{pagesDone}</strong>/{pagesTotal} {aggUnit}
            </span>
          )}
        </div>
        <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
          {startedAt ? `${mins}:${secs.toString().padStart(2, '0')}` : ''}
        </span>
      </div>

      {/* Combined progress bar (pages-based — more granular) */}
      <div
        style={{
          height: 6,
          background: '#0f172a',
          borderRadius: 3,
          overflow: 'hidden',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: `${pagePct}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
            transition: 'width 400ms ease',
          }}
        />
      </div>

      {/* Per-file rows — only when per-file progress data is available */}
      {hasPerFileData && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto' }}>
        {files.map((f) => {
          const fileDone = f.done >= f.total;
          const fileRunning = f.done > 0 && !fileDone;
          const pct = f.total ? (f.done / f.total) * 100 : 0;
          const stem = f.file.replace(/\.pdf$/i, '').replace(/^\d+_/, '');
          return (
            <div
              key={f.file}
              style={{
                display: 'grid',
                gridTemplateColumns: '14px 1fr 70px 90px',
                alignItems: 'center',
                gap: 8,
                padding: '3px 6px',
                borderRadius: 4,
                background: fileRunning ? 'rgba(59,130,246,0.08)' : 'transparent',
              }}
            >
              <span style={{ fontSize: 12, textAlign: 'center', color: fileDone ? '#10b981' : fileRunning ? '#3b82f6' : '#64748b' }}>
                {fileDone ? '✓' : fileRunning ? '⚡' : '○'}
              </span>
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: fileDone ? '#94a3b8' : '#e2e8f0',
                }}
                title={f.file}
              >
                {stem}
              </span>
              <span style={{ color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {f.done}/{f.total}p
              </span>
              <div
                style={{
                  height: 4,
                  background: '#0f172a',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: fileDone ? '#10b981' : '#3b82f6',
                    transition: 'width 400ms ease',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
